import { SuggestionProps } from '@tiptap/suggestion';
import clx from 'classnames';
import * as React from 'react';
import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { IReference } from '../../../references/IReference';
import { ReferenceBadge } from '../../../references/display/ReferenceBadge';
import { ComputeExpressionReferenceAttributes } from '../nodes/ComputeExpressionReferenceNode';
import {
  $ReferenceListItem,
  $ReferenceListPopover,
} from './ComputeExpressionReferenceList.styles';

export interface IComputeExpressionReferenceListProps
  extends SuggestionProps<IReference> {}

export interface ComputeExpressionReferenceListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const ComputeExpressionReferenceList = React.forwardRef<
  ComputeExpressionReferenceListRef,
  IComputeExpressionReferenceListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const displayed = useMemo(() => {
    return {
      items: props.items.slice(0, 10),
      hiddenCount: Math.max(0, props.items.length - 10),
    };
  }, [props.items]);

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      // TODO Al momento sono due tipi diversi (IExpressionReference vs gli `attrs` di ComputeExpressionReferenceNode)
      props.command({
        id: item.code,
        type: item.field.type,
        label: item.label,
        reference: item,
      } as ComputeExpressionReferenceAttributes as any);
    }
  };

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + displayed.items.length - 1) % displayed.items.length,
    );
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % displayed.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <$ReferenceListPopover>
      {displayed.items.map((item, index) => (
        <$ReferenceListItem
          key={item.id}
          onClick={() => selectItem(index)}
          className={clx({ selected: index === selectedIndex })}
        >
          <ReferenceBadge reference={item} selected={index === selectedIndex} />
        </$ReferenceListItem>
      ))}
      {displayed.hiddenCount > 0 && (
        <$ReferenceListItem>
          <span>{`${displayed.hiddenCount} riferimenti nascosti. Inizia a scrivere per filtrare`}</span>
        </$ReferenceListItem>
      )}
    </$ReferenceListPopover>
  );
});

ComputeExpressionReferenceList.displayName = 'ComputeExpressionReferenceList';
